import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { shouldRetryRequest } from "api/apiError";
import { fetchAuditLogs } from "api/auditLogs";
import { fetchRoles, roleKeys } from "api/rbacRoles";
import {
  deleteUser,
  fetchManagedUsers,
  setUserActive,
  userKeys,
  type ManagedUser,
} from "api/users";
import { notify, type SelectOption } from "Components/ui";
import { useAuth } from "hooks/useAuth";

/** How often the recent-activity card reloads. */
const ACTIVITY_REFRESH_MS = 30000;

const activityKey = ["audit-logs", "users-page-activity"] as const;

/** Legacy rows may only have `id`. */
export const getUserId = (user: ManagedUser): string => user._id || user.id || "";

/** Role name from `role`, or the first of the legacy `roles`; "" when the user has none. */
export const getRoleName = (user: ManagedUser): string =>
  user.role?.name || (Array.isArray(user.roles) ? user.roles[0]?.name : "") || "";

/** Role id for the role picker; "" for legacy users, whose roles carry no id. */
export const getRoleId = (user: ManagedUser): string =>
  user.role?._id || (Array.isArray(user.roles) ? user.roles[0]?._id : "") || "";

export const displayName = (user: ManagedUser): string =>
  user.name || user.userName || user.email || "this user";

const roleIncludes = (user: ManagedUser, word: string) =>
  getRoleName(user).toLowerCase().includes(word);

const matchesSearch = (user: ManagedUser, query: string) =>
  [user.userName, user.name, user.email, getRoleName(user), user.phoneNumber].some((value) =>
    (value || "").toLowerCase().includes(query)
  );

const percent = (part: number, total: number) => (total ? Math.round((part / total) * 100) : 0);

export interface RoleMix {
  total: number;
  active: number;
  admins: number;
  support: number;
  /** Percentages of all users; `userPct` is whatever is left after admins and support. */
  adminPct: number;
  supportPct: number;
  userPct: number;
}

export interface UserPermissions {
  canWrite: boolean;
  canApprove: boolean;
  canDelete: boolean;
  canConfigure: boolean;
  canViewAuditLogs: boolean;
}

/** State and API calls for the Users page. */
export const useUsersData = () => {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();

  const permissions: UserPermissions = {
    canWrite: hasPermission("userManagement", "write"),
    canApprove: hasPermission("userManagement", "approve"),
    canDelete: hasPermission("userManagement", "delete"),
    canConfigure: hasPermission("userManagement", "configure"),
    canViewAuditLogs: hasPermission("auditLogs", "read"),
  };
  const { canApprove, canDelete, canViewAuditLogs } = permissions;

  const [search, setSearch] = React.useState("");

  const usersQuery = useQuery({
    queryKey: userKeys.all,
    queryFn: fetchManagedUsers,
    retry: shouldRetryRequest,
  });

  const activityQuery = useQuery({
    queryKey: activityKey,
    queryFn: () => fetchAuditLogs({ actionPrefix: "USER_", limit: 3, page: 1 }),
    enabled: canViewAuditLogs,
    refetchInterval: ACTIVITY_REFRESH_MS,
    retry: shouldRetryRequest,
  });

  const users = React.useMemo(() => usersQuery.data || [], [usersQuery.data]);

  const filteredUsers = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    return query ? users.filter((user) => matchesSearch(user, query)) : users;
  }, [users, search]);

  const roleMix = React.useMemo<RoleMix>(() => {
    const total = users.length;
    const admins = users.filter((user) => roleIncludes(user, "admin")).length;
    const support = users.filter((user) => roleIncludes(user, "support")).length;
    const adminPct = percent(admins, total);
    const supportPct = percent(support, total);
    return {
      total,
      active: users.filter((user) => user.isActive).length,
      admins,
      support,
      adminPct,
      supportPct,
      userPct: total ? Math.max(0, 100 - adminPct - supportPct) : 0,
    };
  }, [users]);

  const refresh = React.useCallback(() => {
    queryClient.invalidateQueries({ queryKey: userKeys.all });
    if (canViewAuditLogs) {
      queryClient.invalidateQueries({ queryKey: activityKey });
    }
  }, [queryClient, canViewAuditLogs]);

  /** Suspends an active user or re-activates a suspended one. Rejects on failure. */
  const toggleActive = React.useCallback(
    async (user: ManagedUser) => {
      if (!canApprove) {
        notify.error("You don't have permission to change a user's status.");
        return;
      }
      const userId = getUserId(user);
      if (!userId) {
        notify.error("This user has no id, so their status can't be changed.");
        return;
      }
      try {
        await setUserActive(userId, !user.isActive);
        notify.success(
          user.isActive ? `${displayName(user)} suspended` : `${displayName(user)} activated`
        );
        refresh();
      } catch (err) {
        notify.error(err, { fallback: "The status wasn't changed." });
        throw err;
      }
    },
    [canApprove, refresh]
  );

  /** Deletes a user for good. Rejects on failure so a confirm dialog stays open. */
  const remove = React.useCallback(
    async (user: ManagedUser) => {
      if (!canDelete) {
        notify.error("You don't have permission to delete users.");
        return;
      }
      const userId = getUserId(user);
      if (!userId) {
        notify.error("This user has no id, so they can't be deleted.");
        return;
      }
      try {
        await deleteUser(userId);
        notify.success(`${displayName(user)} deleted`);
        refresh();
      } catch (err) {
        notify.error(err, { fallback: "The user wasn't deleted." });
        throw err;
      }
    },
    [canDelete, refresh]
  );

  return {
    permissions,
    search,
    setSearch,
    users,
    filteredUsers,
    usersLoading: usersQuery.isLoading,
    usersError: usersQuery.isError ? usersQuery.error : null,
    retryUsers: () => usersQuery.refetch(),
    roleMix,
    activity: {
      entries: activityQuery.data?.data || [],
      // v4: a disabled query reports isLoading, so use isInitialLoading.
      loading: activityQuery.isInitialLoading,
      failed: activityQuery.isError,
    },
    refresh,
    toggleActive,
    remove,
  };
};

export interface RoleOptionsState {
  options: SelectOption[];
  loading: boolean;
  failed: boolean;
  retry: () => void;
}

/** Role picker options for the add and edit panels; loads only while a panel is open. */
export const useRoleOptions = (enabled: boolean): RoleOptionsState => {
  const rolesQuery = useQuery({
    queryKey: roleKeys.all,
    queryFn: fetchRoles,
    enabled,
    retry: shouldRetryRequest,
  });

  const options = React.useMemo<SelectOption[]>(
    () => (rolesQuery.data || []).map((role) => ({ value: role._id, label: role.name })),
    [rolesQuery.data]
  );

  return {
    options,
    loading: rolesQuery.isInitialLoading,
    failed: rolesQuery.isError,
    retry: () => {
      rolesQuery.refetch();
    },
  };
};
